import React, { useEffect, useState } from 'react';
import { Button, Card, CardFooter, Col, Row, Input, FormGroup, Label } from 'reactstrap';
import { endOfDay, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import AdminHeader from '../../../components/header/AdminHeader';
import FormDateInput from '../../../components/form/FormDateInput';
import FormSelect from '../../../components/form/FormSelect';

import {
  dailyAgentReportGet,
  userOptionGet
} from '../../../store/slices/tenantSlices/agentPerformanceSlice';
import { processAllOptionGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import CallexLoader from '../../../components/CallexLoader';
import DailyDetailedReportModal from './DailyDetailedReportModal';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import { checkModulePermission, formatPayloadDate } from '../../../utils/common';
import { toZonedTime } from 'date-fns-tz';
import { useTimezone } from '../../../store/slices/commonSlice';

const DailyAgentReport = () => {
  const dispatch = useDispatch();
  const timezone = useTimezone();
  const today = toZonedTime(new Date(), timezone);
  const userRolePermission = useUserRolePermission();

  const [processOptions, setProcessOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [csvReportData, setCsvReportData] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isCsvDownload, setIsCsvDownload] = useState(false);

  const headerFields = [
    { label: 'Full Name', name: 'user' },
    { label: 'Process', name: 'Process' },
    { label: 'Date', name: 'date' },
    { label: 'No. Of Logins', name: 'total_count' },
    { label: 'First Login Time', name: 'first_login' },
    { label: 'Last Logout Time', name: 'last_logout' },
    { label: 'Working Duration', name: 'total_work_duration' },
    { label: 'Break Duration', name: 'total_break_duration' },
    { label: 'Login Duration', name: 'login_duration' }
  ];

  const csvHeaderFields = [
    { label: 'Full Name', key: 'user' },
    { label: 'Process', key: 'Process' },
    { label: 'Date', key: 'date' },
    { label: 'No. Of Logins', key: 'total_count' },
    { label: 'First Login Time', key: 'first_login' },
    { label: 'Last Logout Time', key: 'last_logout' },
    { label: 'Working Duration', key: 'total_work_duration' },
    { label: 'Break Duration', key: 'total_break_duration' },
    { label: 'Login Duration', key: 'login_duration' }
  ];

  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  const getUserOption = async () => {
    try {
      const response = await dispatch(userOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.Username || '-',
          value: val.id
        }));
        setUserOptions(options);
      } else {
        setUserOptions([]);
      }
    } catch (error) {
      setUserOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getProcessOption();
    getUserOption();
  }, []);

  const initialValues = {
    selected_process_id: [],
    user_ids: [],
    user_login_check: 0,
    is_global: 'no',
    from_date: startOfDay(today),
    to_date: endOfDay(today),
    archiveId: 0
  };

  const validationSchema = Yup.object({
    from_date: Yup.date().required('Start date is required'),
    to_date: Yup.date().required('End date is required'),
    selected_process_id: Yup.array().when('is_global', {
      is: 'no',
      then: (schema) => schema.min(1, 'Process is required').required('Process is required'),
      otherwise: (schema) => schema.notRequired()
    }),
    user_ids: Yup.array().min(1, 'User is required').required('User is required')
  });

  const onSubmit = async () => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const formattedStartDate = formatPayloadDate(values.from_date);
      const formattedEndDate = formatPayloadDate(values.to_date);
      const payload = { ...values };
      payload['from_date'] = formattedStartDate;
      payload['to_date'] = formattedEndDate;
      payload['selected_process_id'] = values?.selected_process_id?.join(',');
      payload['user_ids'] = values?.user_ids?.join(',');
      payload['archiveId'] = 0;

      const response = await dispatch(dailyAgentReportGet(payload)).unwrap();
      if (response) {
        setReportData(response?.data || []);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const { values, handleChange, handleBlur, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  const prepareExportProps = () => {
    const data = csvReportData || [];
    return { headers: csvHeaderFields, data, filename: 'Daily_agent_report.csv' };
  };

  const handleExportCSV = async () => {
    setIsExporting(true);

    try {
      const formattedStartDate = formatPayloadDate(values.from_date);
      const formattedEndDate = formatPayloadDate(values.to_date);
      const payload = { ...values };
      payload['from_date'] = formattedStartDate;
      payload['to_date'] = formattedEndDate;
      payload['selected_process_id'] = values?.selected_process_id?.join(',');
      payload['user_ids'] = values?.user_ids?.join(',');
      payload['archiveId'] = 0;
      const response = await dispatch(dailyAgentReportGet(payload)).unwrap();

      if (response) {
        setCsvReportData(response?.data || []);
        setIsExporting(false);
        setIsCsvDownload(true);
      }
    } catch (error) {
      setIsExporting(false);
      console.log('Error exporting CSV:', error);
    }
  };

  return (
    <div>
      <AdminHeader
        title="Daily Agent Login Logout"
        isSearch
        isExportDisabled={
          !checkModulePermission('is_export', 'Daily Agent Login Logout', userRolePermission)
        }
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isExport={
          values?.user_ids?.length > 0 &&
          values?.selected_process_id?.length > 0 &&
          reportData?.length > 0
        }
        csvProps={prepareExportProps()}
        onExportClick={() => handleExportCSV()}
        setIsCsvDownload={setIsCsvDownload}
        isCsvDownload={isCsvDownload}
        isExporting={isExporting}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="2" md="2" className="flex-row">
                <FormDateInput
                  dateFormat="yyyy-MM-dd"
                  label="From Date"
                  maxDate={today}
                  minDate={today}
                  value={values.from_date}
                  onChange={(e) => setFieldValue('from_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  disabled
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormDateInput
                  label="To Date"
                  dateFormat="yyyy-MM-dd"
                  maxDate={today}
                  minDate={today}
                  value={values.to_date}
                  onChange={(e) => setFieldValue('to_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  disabled
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Process"
                  placeholder="Select Process"
                  value={values.selected_process_id}
                  disabled={values.is_global === 'yes'}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="selected_process_id"
                  options={processOptions}
                  touched={touched}
                  errors={errors}
                  isMulti={true}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="User"
                  placeholder="Select User"
                  value={values.user_ids}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="user_ids"
                  options={userOptions}
                  touched={touched}
                  errors={errors}
                  isMulti={true}
                />
              </Col>
              <Col lg="2" md="2" className="d-flex justify-content-start align-items-center">
                <FormGroup check>
                  <Label check>
                    <Input
                      type="checkbox"
                      id="user_login_check"
                      label="Login User"
                      checked={values?.user_login_check === 0}
                      onChange={() =>
                        setFieldValue('user_login_check', values.user_login_check === 0 ? 1 : 0)
                      }
                    />
                    <Label htmlFor="user_login_check">Login User</Label>
                  </Label>
                </FormGroup>
              </Col>
              <Col lg="2" md="2" className="d-flex justify-content-start align-items-center">
                <FormGroup check>
                  <Label check>
                    <Input
                      type="checkbox"
                      id="is_global"
                      label="Global"
                      checked={values?.is_global === 'yes'}
                      onChange={() => {
                        setFieldValue('is_global', values.is_global === 'yes' ? 'no' : 'yes');
                        if (values.is_global === 'no') {
                          setFieldValue('selected_process_id', []);
                        }
                      }}
                    />
                    <Label htmlFor="is_global">Global</Label>
                  </Label>
                </FormGroup>
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.length > 0 ? (
        <Card className="p-0 position-relative">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 369px)' : 'calc(100vh - 208px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {headerFields?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {reportData?.map((val, index) => {
                  return (
                    <tr
                      key={index}
                      style={{
                        background:
                          val?.user === 'GRAND TOTAL'
                            ? '#2cd07e33'
                            : val?.date === 'TOTAL'
                              ? '#12233533'
                              : 'white'
                      }}>
                      {val?.user === 'GRAND TOTAL' || val?.date === 'TOTAL' ? (
                        <>
                          <td
                            colSpan={Math.min(3, headerFields.length)}
                            className="text-center fw-bolder">
                            {val?.date === 'TOTAL' ? 'TOTAL' : 'GRAND TOTAL'}
                          </td>
                          {headerFields.slice(3).map((field, idx) => (
                            <td
                              className={`${val?.date === 'TOTAL' && field?.name === 'total_count' && val[field?.name] > 0 ? 'text-decoration-underline courser-pointer' : ''}`}
                              key={idx}
                              onClick={() => {
                                if (
                                  field?.name === 'total_count' &&
                                  val[field?.name] > 0 &&
                                  val?.date === 'TOTAL'
                                ) {
                                  setData({ ...val, archiveId: values?.archiveId });
                                  setIsModalOpen(true);
                                }
                              }}>
                              {val[field?.name] || '-'}
                            </td>
                          ))}
                        </>
                      ) : (
                        headerFields?.map((value, idx) => (
                          <td
                            key={idx}
                            className={`${value?.name === 'total_count' && val[value?.name] > 0 ? 'text-decoration-underline courser-pointer' : ''}`}
                            onClick={() => {
                              if (value?.name === 'total_count' && val[value?.name] > 0) {
                                setData({ ...val, archiveId: 0 });
                                setIsModalOpen(true);
                              }
                            }}>
                            {val[value?.name] || '-'}
                          </td>
                        ))
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
      {isModalOpen && (
        <DailyDetailedReportModal
          isOpen={isModalOpen}
          data={data}
          onClose={() => {
            setIsModalOpen(false);
            setData(null);
          }}
        />
      )}
    </div>
  );
};

export default DailyAgentReport;
